import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormInstance } from "antd";
import { message } from "antd";
import type { ParcelCreateAddressModalValues } from "../destination/ParcelCreateAddressModal";
import {
  createParcelTempAddressId,
  isParcelTempAddressId,
} from "../destination/ParcelDestinationAddressCard";
import {
  canSaveRecipientPhone,
  groupParcelAddressesByPhone,
  isSearchPhoneStale,
  normalizePhoneDigits,
  PARCEL_PHONE_MAX_DIGITS,
  PARCEL_PHONE_MIN_SEARCH_DIGITS,
  PHONE_MISMATCH_SEARCH_MSG,
  SEARCH_RECIPIENT_AGAIN_MSG,
  sanitizeParcelPhone,
  sortParcelAddressLookupItems,
  markParcelAddressLatestForDisplay,
  type ParcelPhoneSearchGroup,
} from "../destination/parcelPhoneSearchUtils";
import type { FactoryParcelAddressLookupItem } from "services/factoryParcelApi";
import { useParcelAddressLookup } from "./useParcelAddressLookup";

const applyLookupToForm = (
  form: FormInstance,
  item: FactoryParcelAddressLookupItem
) => {
  const isTemp = isParcelTempAddressId(item.address_id);
  form.setFieldsValue({
    destination: {
      address_id: isTemp ? undefined : item.address_id,
      fullname: item.fullname,
      address: item.address,
      county: item.county,
      city: item.city,
      state: item.state,
      postcode: item.postcode,
      telephone1: item.telephone1,
      province_id:
        item.province_id != null ? Number(item.province_id) : undefined,
      district_id:
        item.district_id != null ? Number(item.district_id) : undefined,
      sub_district_id:
        item.sub_district_id != null ? Number(item.sub_district_id) : undefined,
    },
  });
};

const clearDestinationExceptPhone = (form: FormInstance, phone: string) => {
  form.setFieldsValue({
    destination: {
      telephone1: phone,
      address_id: undefined,
      fullname: undefined,
      address: undefined,
      county: undefined,
      city: undefined,
      state: undefined,
      postcode: undefined,
      province_id: undefined,
      district_id: undefined,
      sub_district_id: undefined,
    },
  });
};

const modalValuesToLookupItem = (
  values: ParcelCreateAddressModalValues,
  tempId: string
): FactoryParcelAddressLookupItem => ({
  address_id: tempId,
  fullname: values.fullname.trim(),
  address: values.address.trim(),
  county: values.county?.trim() ?? "",
  city: values.city?.trim() ?? "",
  state: values.state?.trim() ?? "",
  postcode: String(values.postcode ?? "").trim(),
  telephone1: sanitizeParcelPhone(values.telephone1),
  province_id: values.province_id,
  district_id: values.district_id,
  sub_district_id: values.sub_district_id,
  source: "factory_parcel",
  ref_type: "factory",
  ref_id: "",
  label: "ที่อยู่ใหม่",
  create_date: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

export type UseParcelDestinationSearchOptions = {
  form: FormInstance;
  onAddressChange?: () => void;
};

export function useParcelDestinationSearch({
  form,
  onAddressChange,
}: UseParcelDestinationSearchOptions) {
  const { loading, items: lookupItems, lookupByPhone, clear, cancelInFlight } =
    useParcelAddressLookup();

  const searchSeqRef = useRef(0);

  const [searchPhone, setSearchPhone] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchedDigits, setLastSearchedDigits] = useState("");
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [selectedEntityKey, setSelectedEntityKey] = useState<string | null>(null);
  const [phonePickerOpen, setPhonePickerOpen] = useState(false);
  const [phonePickerGroups, setPhonePickerGroups] = useState<ParcelPhoneSearchGroup[]>([]);
  const [radioSelect, setRadioSelect] = useState("");
  const [localItems, setLocalItems] = useState<FactoryParcelAddressLookupItem[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [noSearchResults, setNoSearchResults] = useState(false);

  const syncSearchPhone = useCallback(
    (digits: string) => {
      const normalized = sanitizeParcelPhone(digits);
      setSearchPhone(normalized);
      form.setFieldValue(["destination", "telephone1"], normalized);
      return normalized;
    },
    [form]
  );

  const invalidateCommittedSearch = useCallback(
    (nextDraftPhone: string) => {
      searchSeqRef.current += 1;
      cancelInFlight();
      setSelectedPhone(null);
      setSelectedEntityKey(null);
      setRadioSelect("");
      setPhonePickerOpen(false);
      setPhonePickerGroups([]);
      setNoSearchResults(false);
      setLocalItems([]);
      clear();
      clearDestinationExceptPhone(form, nextDraftPhone);
      onAddressChange?.();
    },
    [cancelInFlight, clear, form, onAddressChange]
  );

  const handleSearchPhoneChange = useCallback(
    (raw: string) => {
      const normalized = syncSearchPhone(raw);
      if (
        hasSearched &&
        lastSearchedDigits &&
        normalized !== lastSearchedDigits
      ) {
        invalidateCommittedSearch(normalized);
      }
    },
    [
      syncSearchPhone,
      hasSearched,
      lastSearchedDigits,
      invalidateCommittedSearch,
    ]
  );

  const displayItems = useMemo(() => {
    if (!selectedEntityKey && !selectedPhone) return [];
    const byId = new Map<string, FactoryParcelAddressLookupItem>();
    // lookup items: กรองตาม entity (agent-as-anchor — รวมทุกที่อยู่ของ entity นั้น)
    if (selectedEntityKey) {
      const sepIdx = selectedEntityKey.indexOf("|");
      const refType = selectedEntityKey.slice(0, sepIdx);
      const refId = selectedEntityKey.slice(sepIdx + 1);
      for (const item of lookupItems) {
        if (item.ref_type === refType && item.ref_id === refId) {
          byId.set(item.address_id, item);
        }
      }
    }
    // local items (สร้างใหม่จาก modal): ยังกรองด้วยเบอร์เพราะยังไม่มี entity จริง
    for (const item of localItems) {
      if (selectedPhone && normalizePhoneDigits(item.telephone1) === selectedPhone) {
        byId.set(item.address_id, item);
      }
    }
    return markParcelAddressLatestForDisplay(
      sortParcelAddressLookupItems(Array.from(byId.values()))
    );
  }, [lookupItems, localItems, selectedEntityKey, selectedPhone]);

  const selectAddress = useCallback(
    (addressId: string) => {
      const item = displayItems.find((x) => x.address_id === addressId);
      if (!item) return;
      setRadioSelect(addressId);
      applyLookupToForm(form, item);
      onAddressChange?.();
    },
    [displayItems, form, onAddressChange]
  );

  /** เลือกที่อยู่แรกอัตโนมัติหลังเลือก entity — factory_parcel มาก่อนแล้วเรียง create_date */
  useEffect(() => {
    if ((!selectedEntityKey && !selectedPhone) || loading || displayItems.length === 0) return;
    const currentStillValid =
      radioSelect && displayItems.some((x) => x.address_id === radioSelect);
    if (currentStillValid) return;
    selectAddress(displayItems[0].address_id);
  }, [selectedEntityKey, selectedPhone, loading, displayItems, radioSelect, selectAddress]);

  const isSearchStale =
    hasSearched && isSearchPhoneStale(searchPhone, lastSearchedDigits);

  const applyPhoneGroup = useCallback(
    (group: ParcelPhoneSearchGroup) => {
      const pickedPhone = sanitizeParcelPhone(group.telephone1);
      setSelectedPhone(pickedPhone);
      setSelectedEntityKey(group.entity_key);
      setLastSearchedDigits(pickedPhone);
      setNoSearchResults(false);
      setPhonePickerOpen(false);
      setRadioSelect("");
      syncSearchPhone(pickedPhone);
      clearDestinationExceptPhone(form, pickedPhone);
      onAddressChange?.();
    },
    [form, onAddressChange, syncSearchPhone]
  );

  const runAddressSearch = useCallback(
    async (searchDigits: string) => {
      const normalized = sanitizeParcelPhone(searchDigits);
      if (normalized.length < PARCEL_PHONE_MIN_SEARCH_DIGITS) {
        message.warning(
          `กรุณากรอกเบอร์โทรอย่างน้อย ${PARCEL_PHONE_MIN_SEARCH_DIGITS} หลัก`
        );
        return;
      }

      const searchId = ++searchSeqRef.current;
      syncSearchPhone(normalized);
      setHasSearched(true);
      setLastSearchedDigits(normalized);
      setSelectedPhone(null);
      setNoSearchResults(false);
      setRadioSelect("");
      setLocalItems([]);
      setPhonePickerOpen(false);
      setPhonePickerGroups([]);
      clearDestinationExceptPhone(form, normalized);

      const { items: list } = await lookupByPhone(normalized);

      if (searchId !== searchSeqRef.current) {
        return;
      }

      const groups = groupParcelAddressesByPhone(list, normalized);
      setPhonePickerGroups(groups);

      if (groups.length === 0) {
        setNoSearchResults(true);
        if (normalized.length === PARCEL_PHONE_MAX_DIGITS) {
          setSelectedPhone(normalized);
        }
        setCreateModalOpen(true);
        message.info("ไม่พบที่อยู่ในระบบ — โปรดกรอกข้อมูลเพื่อเพิ่มที่อยู่ใหม่");
        onAddressChange?.();
        return;
      }

      setNoSearchResults(false);
      setPhonePickerOpen(true);
      message.success(
        groups.length === 1
          ? "ค้นหาสำเร็จ — เลือกรายการจากหน้าต่าง"
          : `พบ ${groups.length} รายการ — เลือกจากหน้าต่าง`
      );
      onAddressChange?.();
    },
    [clearDestinationExceptPhone, form, lookupByPhone, onAddressChange, syncSearchPhone]
  );

  const handleSearchClick = useCallback(async () => {
    const latestDigits = syncSearchPhone(searchPhone);
    if (latestDigits.length < PARCEL_PHONE_MIN_SEARCH_DIGITS) {
      message.warning(
        `กรุณากรอกเบอร์โทรอย่างน้อย ${PARCEL_PHONE_MIN_SEARCH_DIGITS} หลัก`
      );
      return;
    }
    if (!/^\d+$/.test(latestDigits)) {
      message.warning("กรุณากรอกเฉพาะตัวเลข");
      return;
    }
    await runAddressSearch(latestDigits);
  }, [runAddressSearch, searchPhone, syncSearchPhone]);

  const handlePhoneGroupPick = useCallback(
    (group: ParcelPhoneSearchGroup) => {
      applyPhoneGroup(group);
      message.success("เลือกเบอร์โทรแล้ว — ระบบเลือกที่อยู่แรกให้อัตโนมัติ");
    },
    [applyPhoneGroup]
  );

  const handleCreateModalFinish = useCallback(
    (values: ParcelCreateAddressModalValues) => {
      const phone = sanitizeParcelPhone(values.telephone1);
      if (!canSaveRecipientPhone(phone, lastSearchedDigits)) {
        message.warning(PHONE_MISMATCH_SEARCH_MSG);
        return;
      }
      const tempId = createParcelTempAddressId();
      const item = modalValuesToLookupItem(values, tempId);
      setLocalItems((prev) => [...prev, item]);
      setCreateModalOpen(false);
      setSelectedPhone(phone);
      syncSearchPhone(phone);
      setLastSearchedDigits(phone);
      setNoSearchResults(false);
      setRadioSelect(tempId);
      applyLookupToForm(form, item);
      onAddressChange?.();
      message.success("เพิ่มที่อยู่จัดส่งแล้ว");
    },
    [form, lastSearchedDigits, onAddressChange, syncSearchPhone]
  );

  const handleSearchFromCreateModal = useCallback(
    async (phoneDigits: string) => {
      const normalized = sanitizeParcelPhone(phoneDigits);
      if (!normalized) {
        message.warning("กรุณากรอกเบอร์โทรศัพท์");
        return;
      }
      if (normalized.length < PARCEL_PHONE_MIN_SEARCH_DIGITS) {
        message.warning(
          `กรุณากรอกเบอร์โทรอย่างน้อย ${PARCEL_PHONE_MIN_SEARCH_DIGITS} หลัก`
        );
        return;
      }
      setCreateModalOpen(false);
      await runAddressSearch(normalized);
    },
    [runAddressSearch]
  );

  const canCreateShipAddress =
    hasSearched &&
    !loading &&
    !isSearchStale &&
    (noSearchResults || !!selectedPhone);

  const createModalPhone = noSearchResults ? lastSearchedDigits : selectedPhone ?? "";

  const createModalPhoneEditable =
    noSearchResults && lastSearchedDigits.length < 10 && !isSearchStale;

  const addressEmptyDescription = noSearchResults
    ? "ไม่พบที่อยู่ — กด「เพิ่มที่อยู่จัดส่งเพิ่มเติม」เพื่อเพิ่มที่อยู่ใหม่"
    : !selectedPhone
      ? "เลือกรายการจากผลการค้นหา หรือค้นหาใหม่"
      : displayItems.length === 0
        ? "ไม่มีที่อยู่ กรุณากด「เพิ่มที่อยู่จัดส่งเพิ่มเติม」"
        : "เลือกที่อยู่จัดส่ง";

  const phoneHintText = (() => {
    if (!hasSearched) {
      return `กรอกเบอร์โทรอย่างน้อย ${PARCEL_PHONE_MIN_SEARCH_DIGITS} หลัก (ไม่ต้องครบ ${PARCEL_PHONE_MAX_DIGITS} หลัก) แล้วกด「เพิ่ม/ค้นหา」`;
    }
    if (isSearchStale) return null;
    if (loading) return "กำลังค้นหาที่อยู่...";
    if (noSearchResults) {
      return "ไม่พบที่อยู่จากการค้นหา — เปิดฟอร์มเพิ่มที่อยู่ให้อัตโนมัติ";
    }
    if (!selectedPhone) {
      return "เลือกรายการจากหน้าต่างผลการค้นหา";
    }
    if (displayItems.length > 0 && !radioSelect) {
      return `เบอร์ ${selectedPhone} — เลือกที่อยู่จากการ์ด หรือกด「เพิ่มที่อยู่จัดส่งเพิ่มเติม」`;
    }
    if (selectedPhone && displayItems.length === 0) {
      return `เบอร์ ${selectedPhone} — ไม่พบที่อยู่ กด「เพิ่มที่อยู่จัดส่งเพิ่มเติม」`;
    }
    return null;
  })();

  return {
    loading,
    searchPhone,
    hasSearched,
    lastSearchedDigits,
    selectedPhone,
    phonePickerOpen,
    setPhonePickerOpen,
    phonePickerGroups,
    radioSelect,
    createModalOpen,
    setCreateModalOpen,
    noSearchResults,
    isSearchStale,
    displayItems,
    canCreateShipAddress,
    createModalPhone,
    createModalPhoneEditable,
    addressEmptyDescription,
    phoneHintText,
    searchAgainMessage: SEARCH_RECIPIENT_AGAIN_MSG,
    handleSearchPhoneChange,
    handleSearchClick,
    handlePhoneGroupPick,
    handleCreateModalFinish,
    handleSearchFromCreateModal,
    selectAddress,
  };
}
